import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { BrandLogo, Wordmark } from '@/components/brand/Brand';
import { OfflineBanner, useOnline } from '@/components/ui/Feedback';
import { routes } from '@/routes/paths';
import styles from './AuthLayout.module.css';
import { t } from '@/lib/i18n';
import { ChevronLeftIcon } from '@/components/icons';

interface AuthLayoutProps {
  /** Desktop form heading, and the mobile heading when `compactHero` is set. */
  title: string;
  /** Sub-line under a compact hero, e.g. "Use a mesma conta do relógio". */
  subtitle?: string;
  /**
   * `full` — the branded hero with the monogram (Entrar).
   * `compact` — a back arrow and a title (Criar conta).
   */
  hero?: 'full' | 'compact';
  backTo?: string;
  children: ReactNode;
}

export function AuthLayout({ title, subtitle, hero = 'full', backTo, children }: AuthLayoutProps) {
  const online = useOnline();

  return (
    <div className={styles.page}>
      {/* A connection failure describes a state, so it persists above everything
          rather than firing a toast that vanishes before it is read (canvas 1j). */}
      {!online && <OfflineBanner />}

      {/* ---------------------------------------------------------- mobile */}
      <div className={styles.mobileOnly}>
        {hero === 'full' ? (
          /*
           * O hero do login é só o logo (canvas 1j): 96 px, centrado no verde. O monograma saiu
           * porque a bandeira já está desenhada na arte, e a tagline saiu porque a arte a traz —
           * escrever "Seu jogo, depois da volta" abaixo de "PLAY. TRACK. IMPROVE." seria dizer
           * a mesma coisa duas vezes, em dois idiomas.
           */
          <header className={styles.hero}>
            <h1 className={styles.heroTitle}>
              <BrandLogo height={96} className={styles.heroLogo} />
            </h1>
          </header>
        ) : (
          <header className={`${styles.hero} ${styles.heroCompact}`}>
            {backTo && (
              <Link to={backTo} className={styles.back} aria-label={t('auth.back')}>
                <ChevronLeftIcon size={22} />
              </Link>
            )}
            <h1 className={styles.heroTitle}>{title}</h1>
            {subtitle && <p className={styles.tagline}>{subtitle}</p>}
          </header>
        )}
        <div className={styles.form}>{children}</div>
      </div>

      {/* --------------------------------------------------------- desktop */}
      <div className={styles.split}>
        <section className={styles.promo}>
          <Wordmark height={23} />
          <div className={styles.promoBody}>
            <p className={styles.promoHeadline}>
              {t('auth.hero.line1')}
              <br />
              {t('auth.hero.line2')}
            </p>
            <p className={styles.promoSub}>
              {t('auth.hero.blurb')}
            </p>
          </div>
          <p className={styles.promoFoot}>{t('auth.sameAccountHint')}</p>
        </section>

        <section className={styles.formPane}>
          <div className={styles.formPaneInner}>
            <h1 className={styles.formTitle}>{title}</h1>
            {children}
          </div>
        </section>
      </div>
    </div>
  );
}

/** "Não tem conta? Criar conta" and its inverse. */
export function AuthSwap({
  question,
  linkLabel,
  to,
}: {
  question: string;
  linkLabel: string;
  to: string;
}) {
  return (
    <p className={styles.swap}>
      {question}{' '}
      <Link to={to} className={styles.swapLink}>
        {linkLabel}
      </Link>
    </p>
  );
}

export function SessionLostNotice({ reason }: { reason: 'expired' | 'reuse' }) {
  return (
    <div className={styles.sessionLost} role="status">
      {reason === 'reuse' ? (
        <>
          {t('auth.sessionRevoked')}
        </>
      ) : (
        <>{t('auth.sessionExpired')}</>
      )}
    </div>
  );
}

export { styles as authStyles };
export { routes as authRoutes };
